import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import { Alert, Autocomplete, Box, FormControl, InputLabel, MenuItem, Select, TextField, Typography, } from "@mui/material";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCreateLeagueTeamInvitation, useSearchInvitableTeams, } from "../api/InvitationApiProvider";
import { useRetrieveLeague, useRetrieveLeagueTeams, } from "../api/LeagueApiProvider";
import { useRetrieveUserLeaguePermissions } from "../api/PermissionApiProvider";
import { ROUTES } from "../breadcrumbs/routeConfig";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { TeamAvatar } from "../teams/TeamAvatar";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { FormActions } from "../utils/FormActions";
import { useDebounce } from "../utils/useDebounce";
import { useNumericParams } from "../utils/useNumericParams";
export const SendLeagueInvitation = () => {
    const { id: leagueId } = useNumericParams();
    const navigate = useNavigate();
    const { data: permissions, isLoading: isPermLoading } = useRetrieveUserLeaguePermissions({ leagueId });
    const canModify = permissions?.canModify === true;
    const { data: league, isLoading: isLeagueLoading, error: leagueError, } = useRetrieveLeague({ id: leagueId, enabled: canModify });
    const { data: leagueTeams, isLoading: isTeamsLoading, error: teamsError, } = useRetrieveLeagueTeams({ leagueId, enabled: canModify });
    const [errorMessage, setErrorMessage] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const { data: searchResults, isFetching: isSearchFetching } = useSearchInvitableTeams({
        leagueId,
        searchTerm: debouncedSearchTerm || undefined,
        enabled: canModify,
    });
    const { control, handleSubmit, watch, formState: { errors }, } = useForm({
        defaultValues: {
            teamId: null,
            mergeTeamId: null,
            message: "",
        },
    });
    const createMutation = useCreateLeagueTeamInvitation({
        leagueId,
        onMutate: () => { },
        onSuccess: () => navigate(ROUTES.LEAGUE_INVITATIONS.replace(":id", String(leagueId))),
        onError: (error) => {
            const data = error?.response?.data;
            if (data) {
                const messages = Object.values(data).flat();
                setErrorMessage(messages.join(" "));
            }
            else {
                setErrorMessage("Failed to send invitation.");
            }
        },
    });
    const onSubmit = (data) => {
        if (!data.teamId)
            return;
        createMutation.mutate({
            teamId: data.teamId,
            ...(data.mergeTeamId ? { mergeTeamId: data.mergeTeamId } : {}),
            ...(data.message ? { message: data.message } : {}),
        });
    };
    if (isPermLoading || (canModify && (isLeagueLoading || isTeamsLoading))) {
        return _jsx(Loading, {});
    }
    if (!canModify) {
        return _jsx(Forbidden, {});
    }
    if (leagueError || teamsError || !league) {
        return _jsx(ErrorView, { error: leagueError || teamsError });
    }
    const teamOptions = (searchResults?.results || []).map((t) => ({
        id: t.id,
        label: t.name,
        logo: t.logo,
        primaryColour: t.primaryColour,
        secondaryColour: t.secondaryColour,
    }));
    const mergeableTeams = (leagueTeams || []).map((m) => m.team);
    const selectedTeamId = watch("teamId");
    return (_jsxs(React.Fragment, { children: [_jsxs(Typography, { variant: "h4", gutterBottom: true, children: ["Invite Team \u2014 ", league.name] }), _jsxs(Box, { component: "form", onSubmit: handleSubmit(onSubmit), noValidate: true, role: "form", sx: { maxWidth: 600, mt: 2 }, children: [_jsx(Typography, { variant: "subtitle2", color: "text.secondary", sx: { mb: 2 }, children: "Search for a team to invite to this league." }), _jsx(Controller, { name: "teamId", control: control, rules: {
                            validate: (value) => {
                                if (!value)
                                    return "Please select a team";
                                return true;
                            },
                        }, render: ({ field }) => (_jsx(Autocomplete, { options: teamOptions, getOptionLabel: (option) => typeof option === "string" ? option : option.label, isOptionEqualToValue: (option, value) => option.id === value.id, filterOptions: (x) => x, loading: isSearchFetching, onInputChange: (_e, value) => setSearchTerm(value), onChange: (_e, value) => {
                                field.onChange(value ? value.id : null);
                            }, renderOption: (props, option) => {
                                const { key, ...optionProps } = props;
                                return (_jsxs(Box, { component: "li", ...optionProps, sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(TeamAvatar, { team: {
                                                name: option.label,
                                                logo: option.logo,
                                                primaryColour: option.primaryColour,
                                                secondaryColour: option.secondaryColour,
                                            }, size: 32, showTooltip: false }), option.label] }, key));
                            }, renderInput: (params) => (_jsx(TextField, { ...params, label: "Search for Team", margin: "normal", fullWidth: true, error: !!errors.teamId, helperText: errors.teamId?.message })) })) }), selectedTeamId && mergeableTeams.length > 0 && (_jsxs(Box, { sx: { mt: 2 }, children: [_jsxs(FormControl, { fullWidth: true, children: [_jsx(InputLabel, { children: "Merge Placeholder Team (Optional)" }), _jsx(Controller, { name: "mergeTeamId", control: control, render: ({ field }) => (_jsxs(Select, { ...field, value: field.value ?? "", label: "Merge Placeholder Team (Optional)", children: [_jsx(MenuItem, { value: "", children: _jsx("em", { children: "None" }) }), mergeableTeams.map((team) => (_jsx(MenuItem, { value: team.id, children: team.name }, team.id)))] })) }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { mt: 0.5 }, children: "Optionally select an existing placeholder team whose league data will be merged into the invited team on accept." })] }), watch("mergeTeamId") && (_jsxs(Alert, { severity: "warning", icon: _jsx(MergeTypeIcon, {}), sx: { mt: 2 }, children: [_jsx("strong", { children: "Data merge:" }), " When the invited team accepts, all league data (results, standings, stats) from", " ", _jsx("strong", { children: mergeableTeams.find((t) => t.id === watch("mergeTeamId"))
                                            ?.name }), " ", "will be transferred to the invited team and the placeholder will be removed from the league."] }))] })), _jsx(Controller, { name: "message", control: control, render: ({ field }) => (_jsx(TextField, { ...field, margin: "normal", fullWidth: true, label: "Message (Optional)", multiline: true, rows: 3 })) }), _jsx(FormActions, { submitLabel: "Send Invitation", isSubmitting: createMutation.isLoading })] }), _jsx(ErrorSnackbar, { open: !!errorMessage, onClose: () => setErrorMessage(""), message: errorMessage })] }));
};
